/**
 * The theme picker: eight rows, four of them Premium, and the sentence a
 * lapsed subscriber needs to see.
 *
 * It lists what `GET /api/themes` returned and nothing else. There is no array
 * of theme names in this file, no `isPremium(id)`, and no branch on a plan —
 * the rows come from the `themes` table with an `available` flag already
 * resolved against this account's `premium_themes` limit, so a ninth theme
 * appears here the moment somebody inserts one. See `server/themes.ts`.
 *
 * A locked theme is shown and disabled rather than hidden, for the same reason
 * every other limit in this app greys a control out instead of removing it:
 * "you can't have this" is a useful thing to be told, and "this does not
 * exist" is not the same sentence.
 *
 * ## The lapsed Premium account
 *
 * Somebody who chose Sunset and is now on Free sees a Midnight screen, Sunset
 * still selected in the list, disabled, and a line saying which theme they are
 * actually looking at and why. Their row is untouched, so resubscribing brings
 * Sunset back with nothing to re-pick. The one thing that never happens is the
 * app refusing to draw: `server/themes.ts` has already resolved the effective
 * theme, and `src/theme.css` puts Midnight on bare `:root` besides.
 */

import { useCallback, useEffect, useState } from 'react'
import { chooseTheme, fetchThemes, type ThemesResponse } from '../api'
import { showTheme } from '../theme-document'

export function ThemePicker() {
  const [state, setState] = useState<ThemesResponse | null>(null)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchThemes()
      .then((answer) => {
        if (cancelled) return
        setState(answer)
        // The truth, arriving after the inline script's guess. When they agree
        // — which is every load after the first — this changes nothing and
        // fades nothing.
        showTheme(answer.id)
      })
      .catch(() => {
        // The picker is a preference, not a screen. If it cannot be fetched,
        // the page keeps whatever the inline script put up and says nothing.
      })
    return () => {
      cancelled = true
    }
  }, [])

  const pick = useCallback(
    async (id: string) => {
      if (!state || id === state.id) return
      const previous = state.id
      setBusy(true)
      setFailure(null)
      // Applied before the request, so the cross-fade starts on the click
      // rather than on the round trip. The server is about to agree; if it
      // does not, the line below puts the old one back the same way.
      showTheme(id)
      try {
        setState(await chooseTheme(id))
      } catch (err) {
        showTheme(previous)
        setFailure(err instanceof Error ? err.message : String(err))
      } finally {
        setBusy(false)
      }
    },
    [state],
  )

  if (!state) return null

  const chosen = state.themes.find((theme) => theme.id === state.chosenId)
  const showing = state.themes.find((theme) => theme.id === state.id)

  return (
    <span className="theme">
      <label className="theme__label" htmlFor="theme-picker">
        Theme
      </label>
      <select
        id="theme-picker"
        className="theme__select"
        value={state.id}
        disabled={busy}
        onChange={(event) => pick(event.target.value)}
      >
        {state.themes.map((theme) => (
          <option key={theme.id} value={theme.id} disabled={!theme.available}>
            {theme.name}
            {theme.premium ? ' · Premium' : ''}
          </option>
        ))}
      </select>
      {/*
        The whole lapsed-Premium story, in one line, on the screen it is about.
        `locked` is the server's word for "their choice is out of reach today";
        it never means the page could not be drawn.
      */}
      {state.locked && chosen && (
        <span className="theme__note">
          {chosen.name} is a Premium theme — showing {showing?.name ?? state.id}.
        </span>
      )}
      {failure && <span className="theme__note theme__note--bad">{failure}</span>}
    </span>
  )
}
